// src/pages/component/ComponentEditorModal.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark,
  faCheck,
  faDesktop,
  faTabletAlt,
  faMobileAlt,
  faCopy
} from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from 'react-tooltip';
import {
  Modal,
  Button,
  Tabs,
  Tab,
  Form,
  InputGroup,
  ButtonGroup,
  ToggleButton
} from 'react-bootstrap';

import RichTextEditor from './pagebuilder/RichTextEditor';
import { Dropdown, DropdownButton } from 'react-bootstrap';
import StyleEditor from './StyleEditor';
import { styleConfig } from '../config/styleConfig';
import { templateConfig } from '../config/templateConfig';

import IconCardForm from './componentForms/IconCardForm';
import IconForm from './componentForms/IconForm';
import TextForm from './componentForms/TextForm';
import HeadingForm from './componentForms/HeadingForm';
import ImageForm from './componentForms/ImageForm';
import BrandLogoForm from './componentForms/BrandLogoForm';
import ButtonForm from './componentForms/ButtonForm';
import BlockquoteForm from './componentForms/BlockquoteForm';
import RichTextForm from './componentForms/RichTextForm';
import CardComponentForm from './componentForms/CardComponentForm';
import ImageOverlayCardForm from './componentForms/ImageOverlayCardForm';
import HorizontalCardForm from './componentForms/HorizontalCardForm';
import HeaderFooterCardForm from './componentForms/HeaderFooterCardForm';
import DividerForm from './componentForms/DividerForm';
import ListForm from './componentForms/ListForm';
import AlertForm from './componentForms/AlertForm';
import AudioForm from './componentForms/AudioForm';
import IframeForm from './componentForms/IframeForm';
import VideoForm from './componentForms/VideoForm';
import TabsForm from './componentForms/TabsForm';
import AccordionForm from './componentForms/AccordionForm';
import CarouselForm from './componentForms/CarouselForm';
import ModalForm from './componentForms/ModalForm';
import SpacerForm from './componentForms/SpacerForm';
import BadgeForm from './componentForms/BadgeForm';
import GalleryForm from './componentForms/GalleryForm';
import PricingTableForm from './componentForms/PricingTableForm';
import MapForm from './componentForms/MapForm';
import FAQForm from './componentForms/FAQForm';
import SocialMediaIconsForm from './componentForms/SocialMediaIconsForm';
import CountdownTimerForm from './componentForms/CountdownTimerForm';
import LightboxGalleryForm from './componentForms/LightboxGalleryForm';
import TeamSectionForm from './componentForms/TeamSectionForm';
import FeatureCardsForm from './componentForms/FeatureCardsForm';
import TestimonialForm from './componentForms/TestimonialForm';
import ListGroupForm from './componentForms/ListGroupForm';
import ProgressForm from './componentForms/ProgressForm';
import BreadcrumbForm from './componentForms/BreadcrumbForm';
import ContactFormForm from './componentForms/ContactFormForm';
import ProductListingForm from './componentForms/ProductListingForm';
import BlogListingForm from './componentForms/BlogListingForm';
import MenusForm from './componentForms/MenusForm';
import BlogsForm from './componentForms/BlogsForm';

const ComponentEditorModal = ({ show, handleClose, component, saveChanges }) => {
  const [formValues, setFormValues] = useState({});
  const [styles, setStyles] = useState({ desktop: {}, tablet: {}, mobile: {} });
  const [activeBreakpoint, setActiveBreakpoint] = useState('desktop');
  const [activeTab, setActiveTab] = useState('content');
  const [hydrated, setHydrated] = useState(false);

  const styleSections = useMemo(
    () => (component ? styleConfig[component.type] || {} : {}),
    [component]
  );

  const safeParse = (v) => {
    if (typeof v === 'string') {
      try { return JSON.parse(v); } catch { return v; }
    }
    return v;
  };

  // Normalize props: supports object, JSON string, or { props: "..." }
  const normalizeProps = (raw) => {
    let p = safeParse(raw);
    if (p && typeof p === 'object' && typeof p.props === 'string') {
      const inner = safeParse(p.props);
      if (inner && typeof inner === 'object') p = inner;
    }
    if (!p || typeof p !== 'object') p = {};
    return p;
  };

  // Simple hash for keying child remount
  const hash = (obj) => {
    try {
      const s = typeof obj === 'string' ? obj : JSON.stringify(obj || {});
      let h = 0;
      for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
      return String(h);
    } catch {
      return '0';
    }
  };

  const rawProps = component?.props;
  const childKey = `${component?.id || 'comp'}:${show ? 'open' : 'closed'}:${hash(rawProps)}`;

  // HYDRATE on open + whenever the raw props change (first load after refresh)
  useEffect(() => {
    if (!show || !component) return;

    const parsed = normalizeProps(rawProps);
    setFormValues(parsed);

    const st = parsed.styles && typeof parsed.styles === 'object' ? parsed.styles : {};
    setStyles({
      desktop: st.desktop || {},
      tablet: st.tablet || {},
      mobile: st.mobile || {}
    });

    setHydrated(true);
  }, [show, rawProps, component?.id]); // crucial: include show

  // Reset hydration when modal closes or component changes
  useEffect(() => {
    if (!show) setHydrated(false);
  }, [show]);

  const handleStyleChange = (section, newStyles) => {
    setStyles((prev) => ({
      ...prev,
      [activeBreakpoint]: { ...prev[activeBreakpoint], [section]: newStyles }
    }));

    setFormValues((prevValues) => ({
      ...prevValues,
      styles: {
        ...prevValues.styles,
        [activeBreakpoint]: {
          ...(prevValues.styles ? prevValues.styles[activeBreakpoint] : {}),
          [section]: newStyles
        }
      }
    }));
  };

  const formatLabel = (field) =>
    field.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());

  const handleJsonArrayChange = (fieldName, index, key, value) => {
    const updatedArray = [...(formValues[fieldName] || [])];
    updatedArray[index] = { ...updatedArray[index], [key]: value };
    setFormValues((p) => ({ ...p, [fieldName]: updatedArray }));
  };

  const handleAddJsonArrayItem = (fieldName, newItem) => {
    setFormValues((p) => ({ ...p, [fieldName]: [...(p[fieldName] || []), newItem] }));
  };

  const handleRemoveJsonArrayItem = (fieldName, index) => {
    const updatedArray = (formValues[fieldName] || []).filter((_, i) => i !== index);
    setFormValues((p) => ({ ...p, [fieldName]: updatedArray }));
  };

  const handleSave = () => {
    saveChanges(formValues);
  };

  const handleCopyStyles = () => {
    if (!styles.desktop || Object.keys(styles.desktop).length === 0) {
      alert('No Desktop styles to copy from.');
      return;
    }
    setStyles((prev) => ({ ...prev, [activeBreakpoint]: { ...styles.desktop } }));
    setFormValues((prevValues) => ({
      ...prevValues,
      styles: { ...prevValues.styles, [activeBreakpoint]: { ...styles.desktop } }
    }));
    alert(`Styles copied from Desktop to ${activeBreakpoint}.`);
  };

  const renderJsonArrayFields = (fieldName, itemSchema) => (
    <>
      {formValues[fieldName]?.map((item, index) => (
        <div key={index} className="mb-3">
          {Object.keys(itemSchema).map((key) => (
            <InputGroup key={key} className="mb-2">
              <Form.Control
                type="text"
                placeholder={key}
                value={item[key]}
                onChange={(e) => handleJsonArrayChange(fieldName, index, key, e.target.value)}
              />
            </InputGroup>
          ))}
          <Button variant="danger" onClick={() => handleRemoveJsonArrayItem(fieldName, index)}>
            Remove
          </Button>
        </div>
      ))}
      <Button variant="primary" onClick={() => handleAddJsonArrayItem(fieldName, itemSchema)}>
        Add More
      </Button>
    </>
  );

  if (!component) return null;

  const templatesAvailable = !!templateConfig[component?.type]?.length;
  const stylesAvailable = Object.keys(styleSections).length > 0;

  return (
    <Modal className="model-styling" show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Edit Component "{component?.type}"</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tabs activeKey={activeTab} onSelect={(tab) => setActiveTab(tab || 'content')}>
          <Tab eventKey="content" title="Content">
            {/* Gate child render until hydrated so first paint isn't blank */}
            {!hydrated ? (
              <div className="p-3 text-muted">Loading…</div>
            ) : (
              <div className="">
                {component?.type === 'iconCard' && (
                  <IconCardForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'iconComponent' && (
                  <IconForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'text' && (
                  <TextForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'heading' && (
                  <HeadingForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'image' && (
                  <ImageForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'brandLogo' && (
                  <BrandLogoForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'button' && (
                  <ButtonForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'blockquote' && (
                  <BlockquoteForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'richText' && (
                  <RichTextForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'cardComponent' && (
                  <CardComponentForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'imageOverlayCard' && (
                  <ImageOverlayCardForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'horizontalCard' && (
                  <HorizontalCardForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'headerFooterCard' && (
                  <HeaderFooterCardForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'divider' && (
                  <DividerForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'list' && (
                  <ListForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'alert' && (
                  <AlertForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'audio' && (
                  <AudioForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'iframe' && (
                  <IframeForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'accordion' && (
                  <AccordionForm
                    key={childKey}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    handleAddJsonArrayItem={handleAddJsonArrayItem}
                    handleRemoveJsonArrayItem={handleRemoveJsonArrayItem}
                  />
                )}
                {component?.type === 'carousel' && (
                  <CarouselForm
                    key={childKey}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    handleAddJsonArrayItem={handleAddJsonArrayItem}
                    handleRemoveJsonArrayItem={handleRemoveJsonArrayItem}
                  />
                )}
                {component?.type === 'tabs' && (
                  <TabsForm
                    key={childKey}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    handleAddJsonArrayItem={handleAddJsonArrayItem}
                    handleRemoveJsonArrayItem={handleRemoveJsonArrayItem}
                  />
                )}
                {component?.type === 'gallery' && (
                  <GalleryForm
                    key={childKey}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    handleAddJsonArrayItem={handleAddJsonArrayItem}
                    handleRemoveJsonArrayItem={handleRemoveJsonArrayItem}
                  />
                )}
                {component?.type === 'faq' && (
                  <FAQForm
                    key={childKey}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    handleAddJsonArrayItem={handleAddJsonArrayItem}
                    handleRemoveJsonArrayItem={handleRemoveJsonArrayItem}
                  />
                )}
                {component?.type === 'pricingTable' && (
                  <PricingTableForm
                    key={childKey}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    handleAddJsonArrayItem={handleAddJsonArrayItem}
                    handleRemoveJsonArrayItem={handleRemoveJsonArrayItem}
                  />
                )}
                {component?.type === 'socialMediaIcons' && (
                  <SocialMediaIconsForm
                    key={childKey}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    handleAddJsonArrayItem={handleAddJsonArrayItem}
                    handleRemoveJsonArrayItem={handleRemoveJsonArrayItem}
                  />
                )}
                {component?.type === 'lightboxGallery' && (
                  <LightboxGalleryForm
                    key={childKey}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    handleAddJsonArrayItem={handleAddJsonArrayItem}
                    handleRemoveJsonArrayItem={handleRemoveJsonArrayItem}
                  />
                )}
                {component?.type === 'teamSection' && (
                  <TeamSectionForm
                    key={childKey}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    handleAddJsonArrayItem={handleAddJsonArrayItem}
                    handleRemoveJsonArrayItem={handleRemoveJsonArrayItem}
                  />
                )}
                {component?.type === 'featureCards' && (
                  <FeatureCardsForm
                    key={childKey}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    handleAddJsonArrayItem={handleAddJsonArrayItem}
                    handleRemoveJsonArrayItem={handleRemoveJsonArrayItem}
                  />
                )}
                {component?.type === 'testimonialComponent' && (
                  <TestimonialForm
                    key={childKey}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    handleAddJsonArrayItem={handleAddJsonArrayItem}
                    handleRemoveJsonArrayItem={handleRemoveJsonArrayItem}
                  />
                )}
                {component?.type === 'video' && (
                  <VideoForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'badge' && (
                  <BadgeForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'productList' && (
                  <ProductListingForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'blogList' && (
                  <BlogListingForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'blogs' && (
                  <BlogsForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'menus' && (
                  <MenusForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}
                {component?.type === 'listGroupCard' && (
                  <ListGroupForm
                    key={childKey}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    handleAddJsonArrayItem={handleAddJsonArrayItem}
                    handleRemoveJsonArrayItem={handleRemoveJsonArrayItem}
                  />
                )}
                {component?.type === 'progress' && (
                  <ProgressForm
                    key={childKey}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    handleAddJsonArrayItem={handleAddJsonArrayItem}
                    handleRemoveJsonArrayItem={handleRemoveJsonArrayItem}
                  />
                )}
                {component?.type === 'breadcrumb' && (
                  <BreadcrumbForm
                    key={childKey}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    handleAddJsonArrayItem={handleAddJsonArrayItem}
                    handleRemoveJsonArrayItem={handleRemoveJsonArrayItem}
                  />
                )}

                {component?.type === 'contactForm' && (
                  <ContactFormForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}

                {component?.type === 'map' && (
                  <MapForm key={childKey} formValues={formValues} setFormValues={setFormValues} />
                )}

                {component?.type === 'timeline' &&
                  renderJsonArrayFields('events', { title: '', date: '', description: '' })}
              </div>
            )}

            <Form.Group controlId="skipAiTextGenerator" className="mt-4">
              <Form.Check
                type="checkbox"
                label="Skip AI Text Generator"
                checked={formValues.skipAiTextGenerator || false}
                onChange={(e) =>
                  setFormValues((p) => ({ ...p, skipAiTextGenerator: e.target.checked }))
                }
              />
            </Form.Group>
          </Tab>

          {stylesAvailable && (
            <Tab eventKey="styles" title="Styles">
              <div className="d-flex align-items-center device-view-pop">
                <ButtonGroup>
                  <ToggleButton
                    key="desktop"
                    id="desktop"
                    type="radio"
                    variant={activeBreakpoint === 'desktop' ? 'primary' : 'outline-primary'}
                    name="breakpoint"
                    value="desktop"
                    checked={activeBreakpoint === 'desktop'}
                    onChange={(e) => setActiveBreakpoint(e.currentTarget.value)}
                    data-tooltip-id={`tooltip-desktop-${component.id}`}
                    data-tooltip-content="Desktop"
                  >
                    <FontAwesomeIcon icon={faDesktop} size="lg" />
                  </ToggleButton>
                  <ToggleButton
                    key="tablet"
                    id="tablet"
                    type="radio"
                    variant={activeBreakpoint === 'tablet' ? 'primary' : 'outline-primary'}
                    name="breakpoint"
                    value="tablet"
                    checked={activeBreakpoint === 'tablet'}
                    onChange={(e) => setActiveBreakpoint(e.currentTarget.value)}
                    data-tooltip-id={`tooltip-tablet-${component.id}`}
                    data-tooltip-content="Tablet"
                  >
                    <FontAwesomeIcon icon={faTabletAlt} size="lg" />
                  </ToggleButton>
                  <ToggleButton
                    key="mobile"
                    id="mobile"
                    type="radio"
                    variant={activeBreakpoint === 'mobile' ? 'primary' : 'outline-primary'}
                    name="breakpoint"
                    value="mobile"
                    checked={activeBreakpoint === 'mobile'}
                    onChange={(e) => setActiveBreakpoint(e.currentTarget.value)}
                    data-tooltip-id={`tooltip-mobile-${component.id}`}
                    data-tooltip-content="Mobile"
                  >
                    <FontAwesomeIcon icon={faMobileAlt} size="lg" />
                  </ToggleButton>
                </ButtonGroup>

                {activeBreakpoint !== 'desktop' && (
                  <div className="tooltip-wrapper">
                    <Button
                      variant=""
                      className="copy-button"
                      onClick={handleCopyStyles}
                      aria-label="Copy Styles from Desktop"
                    >
                      <FontAwesomeIcon icon={faCopy} />
                    </Button>
                    <span className="tooltip-text" onClick={handleCopyStyles}>
                      Copy desktop style
                    </span>
                  </div>
                )}

                <Tooltip id={`tooltip-desktop-${component.id}`} place="top" effect="solid" />
                <Tooltip id={`tooltip-tablet-${component.id}`} place="top" effect="solid" />
                <Tooltip id={`tooltip-mobile-${component.id}`} place="top" effect="solid" />
              </div>

              <Tabs defaultActiveKey={Object.keys(styleSections)[0] || 'default'} className="mb-3">
                {Object.keys(styleSections).map((section) => (
                  <Tab eventKey={section} title={formatLabel(section)} key={section}>
                    <StyleEditor
                      styles={styles[activeBreakpoint][section] || {}}
                      onChange={(newStyles) => handleStyleChange(section, newStyles)}
                      editableFields={styleSections[section] || []}
                      activeBreakpoint={activeBreakpoint}
                    />
                  </Tab>
                ))}
              </Tabs>
            </Tab>
          )}

          {templatesAvailable && (
            <Tab eventKey="templates" title="Templates">
              <div className="template-selection">
                {templateConfig[component?.type]?.map((template) => (
                  <div
                    key={template.templateName}
                    className={`template-card ${
                      formValues.template === template.templateName ? 'active' : ''
                    }`}
                    onClick={() =>
                      setFormValues({ ...formValues, template: template.templateName })
                    }
                    style={{
                      cursor: 'pointer',
                      border:
                        formValues.template === template.templateName
                          ? '2px solid #007bff'
                          : '2px solid #ddd'
                    }}
                  >
                    <img src={template.previewImage} alt={template.templateName} />
                    <p>{template.templateName}</p>
                  </div>
                ))}
              </div>
            </Tab>
          )}
        </Tabs>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="danger" onClick={handleClose}>
          <FontAwesomeIcon icon={faXmark} />
        </Button>
        <Button variant="success" onClick={handleSave}>
          <FontAwesomeIcon icon={faCheck} />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ComponentEditorModal;
